import { captureMessage } from "../sentry";

const OPENAI_PIXEL_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

/**
 * Install the OpenAI (ChatGPT) Ads Measurement Pixel. Same loader as the
 * official snippet, but taking the Pixel ID from the environment so only
 * production measures. The marketing site runs the same pixel, and its
 * ChatGPT-ad click cookie (`__oppref`) is shared on .migiri.app, so the click
 * captured there is attributed here.
 */
export const loadOpenAiPixel = (pixelId: string | undefined) => {
  if (!pixelId || window.oaiq) {
    return;
  }

  const oaiq = ((...args: unknown[]) => {
    (oaiq.q = oaiq.q || []).push(args);
  }) as OpenAiPixel;
  window.oaiq = oaiq;
  oaiq("init", { pixelId });

  const script = document.createElement("script");
  script.async = true;
  script.src = OPENAI_PIXEL_SDK_URL;
  document.head.appendChild(script);
};

const sha256Hex = async (value: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

/**
 * Re-init the pixel with the signed-in user so OpenAI can match conversions.
 * The pixel only accepts SHA-256 hashes, never the raw email or uid.
 */
export const identifyOpenAiPixelUser = async (uid: string, email?: string | null) => {
  if (!window.oaiq) {
    return;
  }

  try {
    const normalizedEmail = email?.trim().toLowerCase();
    const user: OpenAiPixelUser = { external_id_sha256: await sha256Hex(uid.trim()) };
    if (normalizedEmail) {
      user.email_sha256 = await sha256Hex(normalizedEmail);
    }
    window.oaiq("init", { user });
  } catch (error) {
    captureMessage(`identifyOpenAiPixelUser: ${error}`, "warning");
  }
};

/**
 * The setup intent id is unique per trial, so OpenAI drops any repeated send
 * of the same conversion.
 */
export const reportOpenAiPixelTrialStarted = (eventId: string, planId: string) => {
  window.oaiq?.("measure", "trial_started", { type: "plan_enrollment", plan_id: planId }, { event_id: eventId });
};
